import { giphyDarkestGrey } from '@giphy/js-brand'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, BORDER_RADIUS_SMALL } from '../../../../constants'
import { useCreationSourcePreviewUrl } from '../../../../hooks/use-creation-source-preview-url'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolStatus from '../../../../types/status'
import { validateSourceByOutput } from '../../../../utils/validate-source'
import ErrorModal from '../../../error-modal'
import { CreationToolUploadContext } from '../../manager'
import { CreationToolUploadSource } from '../../types'
import Buttons from './buttons'
import Fields from './fields'
import Icon from './icon'
import Label from './label'
import PreviewComponent from './preview'
import StatusBar from './status-bar'

const Container = styled.div`
    background: ${giphyDarkestGrey};
    border-radius: ${BORDER_RADIUS_SMALL}px;
    margin-bottom: 5px;
    min-height: 50px;
    position: relative;
`

const Header = styled.div`
    align-items: center;
    display: flex;
    height: 50px;
    position: relative;
    width: 100%;
`

const PreviewToggleHitZone = styled.div`
    cursor: pointer;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
`

const Error = styled(ErrorModal)`
    background: rgba(0, 0, 0, 0.9);
    box-shadow: 0 0 9px rgba(0, 0, 0, 0.5);
    margin: 15px;
`

const Preview = styled(PreviewComponent)`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    margin-top: 5px;
    overflow: hidden;
`

type Props = {
    source: CreationToolUploadSource
}

const CreationUploadItem = ({ source }: Props) => {
    const { source: src } = source
    const { status: uploadStatus, uploadSources, removeUploadSource, updateUploadSource } = useContext(
        CreationToolUploadContext
    )
    const [isPreviewing, setIsPreviewing] = useState<boolean>(false)
    const previewUrl = useCreationSourcePreviewUrl(src.source)
    const { error, status, id: sourceId, uploadedId } = source
    const isUploading = uploadStatus !== CreationToolStatus.Idle
    const isBulk = uploadSources.length > 1

    useEffect(() => {
        if (!isBulk) {
            setIsPreviewing(true)
        } else if (uploadStatus !== CreationToolStatus.Idle) {
            setIsPreviewing(false)
        }
    }, [isBulk, uploadStatus])

    const onChangeOutputType = async (outputType: CreationToolOutputType) => {
        const validatedFile = await validateSourceByOutput(src.source, outputType)
        updateUploadSource({
            ...source,
            source: validatedFile,
            error: undefined,
            outputType,
        })
    }

    const onRetry = () => {
        updateUploadSource({
            ...source,
            error: undefined,
        })
    }

    return (
        <Container
            style={
                isUploading && status === CreationToolStatus.Idle && !uploadedId
                    ? { opacity: 0.5, pointerEvents: 'none' }
                    : {}
            }
        >
            <Header>
                <Icon source={source} />
                {isBulk && <PreviewToggleHitZone onClick={() => setIsPreviewing(!isPreviewing)} />}
                <Label
                    source={source}
                    disabled={!!error || !!uploadedId || status !== CreationToolStatus.Idle || isUploading}
                />
                <Buttons
                    {...{
                        source,
                        isBulk,
                        isPreviewing,
                        onTogglePreview: isPreviewing => setIsPreviewing(isPreviewing),
                    }}
                />
            </Header>
            <StatusBar status={status} isError={!!error} />
            {isPreviewing && (
                <>
                    <Preview source={source} previewUrl={previewUrl}>
                        {error && (
                            <Error
                                {...{
                                    error,
                                    sourceId: src.id,
                                    stickerDisplayMode: 'small',
                                    goBackLabel: `Remove`,
                                    onGoBack: () => removeUploadSource(sourceId),
                                    onChangeOutputType,
                                    onRetry,
                                }}
                            />
                        )}
                    </Preview>
                    {!error && (
                        <Fields
                            {...{
                                source,
                                isBulk,
                            }}
                        />
                    )}
                </>
            )}
        </Container>
    )
}

export default CreationUploadItem
